// item-completion.js — completion-indicator policy for lesson-item rows.
//
// Brief 099 §5. Two questions per row, answered here so the screen stays thin:
//   1. Is this item COMPLETABLE — does it show a play-count / ✓ indicator?
//   2. What is its requiredPlay (the Y in the "X/Y" arc)?
//
// PEDAGOGY OWNER: the course designer (CLAUDE.md Rule #8). `requiredPlay` is a
// PER-ITEM, course-designer-authored value (ratified 2026-06-02: "Course
// designer decides what required playcount is for each lesson item"). Both the
// authored baseline AND the meter rule's playcount increment are designer
// signals; the resolution below honours BOTH:
//
//   requiredPlay  =  max( item.requiredPlay (authored CMS/pathway field),    ← course designer
//                         itemProgress.requiredPlay (meter-bumped, Brief 055/125) )
//                 || per-type default (1)
//
// Brief 125 reconciliation: iOS keeps ONE `required_play` column — the authored
// value SEEDS it and a Listening/Dialogue meter BUMPS it via
// `max(required_play, completed) + delta` (KFEDatabaseManager.m:150), then the
// lesson chart + gate read that same bumped column. The web split that column
// into authored (pathway JSON, Brief 100) + per-learner (itemProgress, the
// meter bump). The pre-125 order `authored ?? progress` let the authored value
// MASK every bump — the song stayed at its authored 3 no matter how low the
// learner metered, so the bump wrote nowhere observable (Brief 125 symptom).
// Taking the MAX restores iOS's single-column behaviour: authored is the floor
// (Rule #8 — a bump can only raise it, never undercut the designer), and a meter
// bump (which produces a per-learner value above the authored floor) takes
// effect on both the X/Y arc and gating. The bump itself is seeded from the
// authored baseline in applyPlaycountAdjustment so +delta lands on top of it.
//
//   completable   =  authored requiredPlay present → (requiredPlay >= 1)   ← authoritative
//                 =  authored requiredPlay absent   → type in COMPLETABLE_TYPES (fallback)
//   (requiredPlay === 0 means content — NO indicator — even for an exercise type.)
//
// The default per-type policy mirrors V1-UI-DESIGN-NOTES §7.2 (and the Brief 099
// gotcha): the indicator is for "do-an-action" items — exercises, dialogues,
// recordings, tests — NOT for passive content (instructional video, plain audio,
// reference notes, meditations, songs). A ✓ on a video would wrongly imply the
// video is "done"; videos are watch-anytime reference. The course designer can
// flip ANY individual item completable by authoring `requiredPlay` on it.
//
// DATA NOTE (Brief 100, was Brief 073): per-item `requiredPlay` NOW SHIPS on
// every non-gate lesson item in content/pathways/<id>/lessons/*.json — joined
// per (folder, item) from the canonical master_folder_items + the iOS type-gate
// (KFEContentUpdateApi.m:185-196), reproducing the exact value the iOS lesson
// chart renders. So `item.requiredPlay` is now the authoritative, populated
// path: the song reads "0/3", the reference (rp=0) shows no indicator, HAAI
// reads "0/1". The COMPLETABLE_TYPES type-set below is now a TRUE LAST-RESORT
// fallback — it fires only for an item that reaches this policy WITHOUT a
// numeric `requiredPlay` (e.g. a menu-surface item, a hand-built test fixture,
// or pre-regeneration stale JSON). For pathway lesson rows it no longer
// governs. (Brief 073 era: the export carried none, so every completable item
// fell back to requiredPlay = 1 / the type set.)

import {
  ITEM_TYPE_GATE,
  ITEM_TYPE_AUDIO,
  ITEM_TYPE_PHRASE_AUDIO,
  ITEM_TYPE_DIALOGUE_VARIANT,
} from "./content-items.js";

/**
 * Content types that route to <kfe-audio-exercise> (the host's Brief 132 audio
 * arm): 2 plain audio, 5 phrase audio, 12 situation-dialogue variant. These are
 * the web port's analogue of the iOS MediaViewController path, where every
 * <audio> `ended` is a legitimate play-end that increments the item's playcount
 * (MediaViewController.m:1535 `incrementItemPlaycount`, no per-view guard).
 *
 * Brief 139 uses this so the exercise host relaxes its one-shot completion guard
 * for audio items ONLY: an audio item's repeated play-ends each advance
 * `completedCount`, while every other exercise screen — which emits
 * `item_completed` exactly once at its internal completion point — keeps the
 * Brief 060b single-write-per-view safety net.
 */
const AUDIO_CONTENT_TYPES = new Set([
  ITEM_TYPE_AUDIO,
  ITEM_TYPE_PHRASE_AUDIO,
  ITEM_TYPE_DIALOGUE_VARIANT,
]);

/**
 * @param {number|string|null|undefined} type a content item's numeric `type`.
 * @returns {boolean} true if the type is an audio-player content type whose
 *   every play-end is a per-play increment (Brief 139).
 */
export function isAudioContentType(type) {
  return AUDIO_CONTENT_TYPES.has(/** @type {any} */ (type));
}

/**
 * Lesson-JSON string types that are completable by default (no authored
 * requiredPlay needed). Numeric equivalents from kfe_content_items.json are
 * mapped alongside so both data shapes resolve.
 *
 * Completable: HAAI, L2B Training, L2B Testing, Self-Recording, Multiple
 * Choice, True/False, Situation Dialogue.
 * NOT (content): video_lesson, audio_video, reading_lesson, reference_note,
 * music_track, meter_test, gate, intro.
 */
const COMPLETABLE_TYPES = new Set([
  "haai_exercise",
  "l2b_exercise",
  "l2b_test",
  "recording_exercise",
  "multiple_choice",
  "true_false",
  "situation_dialogue",
  // numeric (content-items.js): 7 HAAI, 9 L2B, 10 L2B-test, 11 SR, 12 dialogue,
  // 13 MC, 14 TF.
  7, 9, 10, 11, 12, 13, 14,
]);

/**
 * @typedef {object} ItemLike
 * @property {string|number} [type]
 * @property {number} [requiredPlay]   authored per-item override (CMS canon)
 */

/**
 * @param {ItemLike} item
 * @returns {boolean}
 */
export function isCompletableItem(item) {
  if (!item) return false;
  /** @type {any} */ const type = item.type;
  if (type === "gate" || type === ITEM_TYPE_GATE) return false;
  // Authored requiredPlay is AUTHORITATIVE (iOS canon — confirmed against the
  // CMS item editor 2026-06-02): >= 1 → completable with that target; exactly 0
  // → content, NO indicator (e.g. a reference eBook). When an explicit value is
  // present it overrides the type heuristic entirely — type is NOT a reliable
  // discriminator (a "Rhythmic English Song" carries requiredPlay 3, an
  // audiobook carries 1, a reference carries 0; `master_folder_items` proves
  // ~5% of items even differ per containing folder).
  if (typeof item.requiredPlay === "number") return item.requiredPlay >= 1;
  // Last-resort fallback ONLY — pathway lesson items now ship requiredPlay
  // (Brief 100), so this fires only for items that arrive WITHOUT it (menu
  // surfaces, fixtures, pre-regeneration stale JSON). Approximate completability
  // with the type set below.
  return COMPLETABLE_TYPES.has(type);
}

/**
 * Resolve the effective requiredPlay (Y) for an item as the MAX of the authored
 * baseline and the meter-bumped per-learner value (Brief 125 — see the header
 * note). `progressRequired` is the itemProgress.requiredPlay the learner-store
 * carries (meter-bumped); pass it when available. Floors at 1 so a completable
 * item never reads "X/0".
 *
 * @param {ItemLike} item
 * @param {number} [progressRequired]
 * @returns {number}
 */
export function resolveRequiredPlay(item, progressRequired) {
  const authored = (item && typeof item.requiredPlay === "number") ? item.requiredPlay : 0;
  const fromProgress = (typeof progressRequired === "number") ? progressRequired : 0;
  const y = Math.max(authored, fromProgress);
  return Math.max(1, Math.floor(y));
}

/**
 * Brief 137 — meter dispatch completion gate. A meter that follows an item in
 * the lesson sequence must fire ONLY once that predecessor has been played to
 * its full required count, NOT on every play-end.
 *
 * iOS canon (MediaViewController.m:1733): the meter VC is presented inside
 * `if (numberOfCompletions == requiredPlay) { … }` — strictly when the
 * post-increment completion count reaches the target. We mirror with a
 * defensive `>=` rather than `==`: the web port can have `completedCount` exceed
 * the target in edge cases iOS cannot hit (a mid-flight bump from an EARLIER
 * meter, an async completion race), and `completed == required` always implies
 * `completed >= required`, so `>=` is canon-compatible and strictly safer.
 *
 * The threshold is `resolveRequiredPlay` (Brief 125's `max(authored, bumped)`)
 * so a bump raised by an earlier meter is honoured as the gate target instead
 * of being masked by the stale authored read. This is a PEDAGOGY gate (Rule #8),
 * independent of the navigation-gating system — dev-mode "gating off" does NOT
 * relax it, because nothing here consults gatingEnabled.
 *
 * @param {number} completedCount the predecessor's post-increment play count
 *   (itemProgress.completedCount).
 * @param {ItemLike} item the predecessor item (carries the authored requiredPlay).
 * @param {number} [progressRequired] the meter-bumped itemProgress.requiredPlay.
 * @returns {boolean} true → predecessor fully played; the meter may dispatch.
 */
export function meterDispatchAllowed(completedCount, item, progressRequired) {
  return (completedCount ?? 0) >= resolveRequiredPlay(item, progressRequired);
}
